import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo } from 'react';
import { useMount } from 'react-use';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { IdentityDocument } from '@dreryk/egabinet-patient-published-language-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';

import {
   createIdentityDocumentTypeVisitor,
   IdentityDocumentType,
   IdentityDocumentTypeField,
   IdentityDocumentTypeVisitor,
} from '../../selector/IdentityDocumentTypeField';
import { IdentityDocumentValueField } from '../../selector/IdentityDocumentValueField';
import { PassportCountryField } from '../../selector/PassportCountryField';
import { PassportValidToField } from '../../selector/PassportValidToField';

const StyledColumn = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   column-gap: 24px;
`;

StyledColumn.displayName = 'StyledColumn';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: 1fr 1fr;
   column-gap: 36px;
   row-gap: 24px;
`;

StyledForm.displayName = 'StyledForm';

export type PassportIdentityDocumentEditFormBean = {
   identityDocumentType: IdentityDocumentType;
   identityDocumentValue: string | null;
   passportCountry: Country | null;
   passportValidTo: LocalDate | null;
};

export type PassportIdentityDocumentEditFormController = FormContextValues<PassportIdentityDocumentEditFormBean>;

declare type PassportIdentityDocumentEditFormRendererProps = Pick<
   PassportIdentityDocumentEditFormController,
   'handleSubmit' | 'formState'
>;

declare type PassportIdentityDocumentEditFormRenderer = (
   renderProps: PassportIdentityDocumentEditFormRendererProps
) => JSX.Element;

type PassportIdentityDocumentEditFormProps = {
   readonly identityDocument: IdentityDocument;
   readonly children: PassportIdentityDocumentEditFormRenderer;
};

// eslint-disable-next-line max-lines-per-function
export const PassportIdentityDocumentEditForm = (props: PassportIdentityDocumentEditFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState,
      setValue,
      setFocus,
      watch,
      trigger,
   } = useFormController<PassportIdentityDocumentEditFormBean>({
      identityDocumentType: 'Passport',
      identityDocumentValue: props.identityDocument.getSeriesAndNumber(),
      passportCountry: props.identityDocument.getIssuingCountry(),
      passportValidTo: props.identityDocument.getValidTo(),
   });

   const { identityDocumentType } = watch(['identityDocumentType']);

   const identityDocumentTypeVisitor = useMemo<IdentityDocumentTypeVisitor>(
      () => createIdentityDocumentTypeVisitor(identityDocumentType),
      [identityDocumentType]
   );

   const onIdentityDocumentType = useCallback(() => {
      setValue('identityDocumentValue', null);
      setFocus('identityDocumentValue');
   }, [setValue, setFocus]);

   useMount(() =>
      identityDocumentTypeVisitor<void>({
         visitPassport: () => {
            trigger('passportValidTo');
            setFocus('passportValidTo');
         },
      })
   );

   return (
      <>
         <StyledForm noValidate autoComplete={'off'}>
            <IdentityDocumentTypeField
               name={'identityDocumentType'}
               register={register}
               onSelect={onIdentityDocumentType}
               required
            />
            <IdentityDocumentValueField
               name={'identityDocumentValue'}
               register={register}
               identityDocumentTypeVisitor={identityDocumentTypeVisitor}
               required
            />
            {identityDocumentTypeVisitor({
               visitPassport: () => (
                  <>
                     <PassportCountryField name={'passportCountry'} register={register} required />
                     <PassportValidToField name={'passportValidTo'} register={register} required />
                  </>
               ),
            })}
         </StyledForm>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </>
   );
};
